import {Popover, Tag, Input, Button, Modal, Table, Tooltip, Row, Alert, Radio} from "antd";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import {toTurkishProperCase} from "utils/toTurkishProperCase";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";
import React, {useEffect, useState} from "react";
import moment from "moment";
import {Link} from "react-router-dom";
import {
    courierRequestCourierApprovalStatues,
    courierRequestCourierApprovalStatuses,
    courierRequestPriorities,
    courierRequestPriorityRankIconKeys
} from "constants/constants";
import {RankIcon} from "components/_defaultComponents/RankIcon/RankIcon";
import {MissingCourierFieldsModal} from "components/_defaultComponents/MissingCourierFieldsModal/MissingCourierFieldsModal";

const BASE_DEDICATION_REQUIRED_FIELDS = ["ossNumber", "projectInfo", "managerCompany"];

export const CourierRequestSelection = ({courier, callbackFn = () => {}}) => {
    const [visible, setVisible] = useState(false);
    const stores = useStore();
    const [selectedCourierRequestId, setSelectedCourierRequestId] = useState(
        courier?.courierRequestInfo?.courierRequestId?.toString()
    );
    const [missingFields, setMissingFields] = useState([]);

    const [selectedCourierRequestApproveStatus, setSelectedCourierRequestApproveStatus] = useState(
        courier?.courierRequestInfo?.requestApproveStatus ||
            courierRequestCourierApprovalStatues.APPROVED
    );
    const [courierRequestSelectionLoading, setCourierRequestSelectionLoading] = useState(false);

    const [loading, availableCourierRequests] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.courierRequestStore.listAvailableCourierRequestsByCourierId.bind(null, {
            courierId: courier._id,
            skip: 0,
            limit: 10,
            paginate: false
        }),
        dependencies: [courier?._id],
        initialCall: true,
        condition: Boolean(courier?._id)
    });

    const isInProjectPhase = () =>
        courier?.activationStep === "candidate" && courier?.activationPhase === "project";

    const checkCourierDedicationRequiredFields = ({courier = {}, company = {}}) => {
        try {
            const companyCourierDedicationRequiredFields =
                company?.courierDedicationRequiredFields || [];
            const courierDedicationRequiredFields = new Set([
                ...BASE_DEDICATION_REQUIRED_FIELDS,
                ...companyCourierDedicationRequiredFields
            ]);

            const _missingFields = [];

            courierDedicationRequiredFields?.forEach(field => {
                if (
                    courier?.[field] === undefined ||
                    courier?.[field] === null ||
                    courier?.[field] === ""
                ) {
                    _missingFields.push(field);
                }
            });
            setMissingFields(_missingFields || []);
            if (_missingFields.length > 0) {
                return {success: false, message: "Eksik Kurye Bilgileri"};
            }

            return {success: true};
        } catch (error) {
            return {success: false, message: error.message};
        }
    };

    const handleSelectCourierRequest = async () => {
        setCourierRequestSelectionLoading(true);
        setMissingFields([]);
        try {
            /**
             * Kurye, kurye talebine onaylı olarak linkleniyorsa, kurye bilgileri kontrol edilir.
             * Seçilen firmaının kurye atama kurallarına uygun olup olmadığı kontrol edilir.
             * Eksik kurye bilgileri varsa, eksik kurye bilgileri modalı gösterilir ve doldurulması beklenir.
             */
            if (
                selectedCourierRequestApproveStatus ===
                courierRequestCourierApprovalStatues.APPROVED
            ) {
                const courierRequest = availableCourierRequests?.data?.find(
                    request => request?._id?.toString() === selectedCourierRequestId?.toString()
                );
                if (!courierRequest) {
                    throw new Error("Kurye talebi bulunamadı");
                }
                const checkCourierDedicationRequiredFieldsResult = checkCourierDedicationRequiredFields(
                    {
                        courier: courier,
                        company: courierRequest?.company || {}
                    }
                );

                if (!checkCourierDedicationRequiredFieldsResult?.success) {
                    return;
                }
            }

            await stores.courierRequestStore.linkCourierToCourierRequest({
                courierId: courier._id,
                courierRequestId: selectedCourierRequestId,
                requestApproveStatus: selectedCourierRequestApproveStatus
            });
            setCourierRequestSelectionLoading(false);
            setVisible(false);
            callbackFn();
        } catch (error) {
            setCourierRequestSelectionLoading(false);
        } finally {
            setCourierRequestSelectionLoading(false);
        }
    };
    useEffect(() => {
        if (visible) {
            setSelectedCourierRequestApproveStatus(
                courier?.courierRequestInfo?.requestApproveStatus ||
                    courierRequestCourierApprovalStatues.APPROVED
            );
            setSelectedCourierRequestId(courier?.courierRequestInfo?.courierRequestId?.toString());
        }
    }, [visible]);

    const canLinkCourierToCourierRequest = () =>
        stores.meStore.checkRole("linkCourierToCourierRequest") &&
        stores.meStore.checkPermission("couriers.edit");

    const canSelectCourierRequest = () => isInProjectPhase() && canLinkCourierToCourierRequest();

    if (!isInProjectPhase() && !courier?.courierRequestInfo?.courierRequestId) {
        return null;
    }

    return (
        <>
            <Tooltip
                title={
                    !isInProjectPhase()
                        ? "Kuryenin tüm bilgileri onaylanmadan proje seçimi yapılamaz. Kurye, proje fazında değil."
                        : !canLinkCourierToCourierRequest()
                        ? "Kurye Talebi Seçilmemiş"
                        : "Kurye Talebi Seç"
                }
            >
                <Tag
                    color={canSelectCourierRequest() ? "black" : "gray"}
                    onClick={() => (canSelectCourierRequest() ? setVisible(true) : null)}
                    style={{
                        cursor: canSelectCourierRequest() ? "pointer" : "default",
                        marginRight: "4px"
                    }}
                    disabled={true}
                >
                    {!canLinkCourierToCourierRequest()
                        ? "Kurye Talebi Seçilmemiş"
                        : courier?.courierRequest?.name || "Kurye Talebi Seç"}
                </Tag>
            </Tooltip>

            <Modal
                title="Kurye Talebi Seç"
                visible={visible}
                onCancel={() => setVisible(false)}
                onOk={handleSelectCourierRequest}
                okText="Seç"
                okButtonProps={{
                    disabled:
                        !selectedCourierRequestId ||
                        !selectedCourierRequestApproveStatus ||
                        courierRequestSelectionLoading,
                    loading: courierRequestSelectionLoading
                }}
                cancelText="İptal"
                cancelButtonProps={{
                    disabled: courierRequestSelectionLoading,
                    loading: courierRequestSelectionLoading
                }}
                width={1000}
            >
                <Row style={{marginBottom: 16}}>
                    <Alert
                        showIcon
                        message="Kurye Talebi Seçimi Hakkında"
                        description={
                            <>
                                Seçilecek kurye talebi, kurye ile eşleştirilecektir. <br /> Kurye
                                talebi seçildikten sonra, otomatik olarak kuryenin firma-alt firma
                                bilgileri güncellenecektir.
                                <br /> Aşağıda, kurye için uygun talepler listelenmiştir.
                            </>
                        }
                        type="info"
                        style={{width: "100%"}}
                    />
                </Row>
                <Row>
                    <Alert
                        description={
                            <>
                                <p>
                                    Kurye talebi seçimi yaparken, durum bilgisi seçimi de
                                    yapılmalıdır.
                                    <br />{" "}
                                    <b>
                                        {
                                            courierRequestCourierApprovalStatuses?.find(
                                                status =>
                                                    status.value ===
                                                    courierRequestCourierApprovalStatues.APPROVED
                                            )?.name
                                        }
                                    </b>{" "}
                                    olarak seçim yapılması durumunda, kurye talebi seçimi geri
                                    alınamaz.
                                </p>

                                <Radio.Group
                                    disabled={
                                        courier?.courierRequestInfo?.requestApproveStatus ===
                                        courierRequestCourierApprovalStatues.APPROVED
                                    }
                                    value={selectedCourierRequestApproveStatus}
                                    onChange={e =>
                                        setSelectedCourierRequestApproveStatus(e.target.value)
                                    }
                                >
                                    {courierRequestCourierApprovalStatuses?.map(status => (
                                        <Radio key={status.value} value={status.value}>
                                            <b>{status.name}</b>
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </>
                        }
                        type="warning"
                        style={{width: "100%", marginBottom: 16}}
                    />
                    <Table
                        rowSelection={{
                            selectedRowKeys: [selectedCourierRequestId],
                            onChange: selectedRowKeys => {
                                setSelectedCourierRequestId(
                                    selectedRowKeys[selectedRowKeys.length - 1]
                                );
                            },
                            hideSelectAll: true
                        }}
                        scroll={{x: "max-content"}}
                        dataSource={availableCourierRequests?.data || []}
                        loading={loading}
                        rowKey="_id"
                        pagination={false}
                    >
                        <Table.Column
                            dataIndex={["createdAt"]}
                            title="Tarih"
                            ellipsis={{
                                tooltip: true
                            }}
                            width={100}
                            render={createdAt => (
                                <Tooltip title={moment(createdAt).format("DD.MM.YYYY HH:mm:ss")}>
                                    {moment(createdAt).format("DD.MM.YYYY")}
                                </Tooltip>
                            )}
                        />
                        <Table.Column
                            dataIndex={["priority"]}
                            title="Öncelik"
                            ellipsis={{
                                tooltip: true
                            }}
                            width={50}
                            align="center"
                            render={(priority, row) => (
                                <Tooltip
                                    title={
                                        courierRequestPriorities.find(p => p.value === priority)
                                            ?.name
                                    }
                                >
                                    <span>
                                        <RankIcon
                                            style={{fontSize: "24px"}}
                                            rankType={
                                                courierRequestPriorityRankIconKeys?.[priority]
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            )}
                        />
                        <Table.Column
                            dataIndex={["name"]}
                            title="İsim"
                            width={150}
                            render={name => <Shortener text={name} length={32} />}
                        />
                        <Table.Column
                            dataIndex={["zone", "name"]}
                            title="Bölge"
                            ellipsis={{
                                tooltip: true
                            }}
                            width={50}
                            render={zoneName => <Shortener text={zoneName} length={32} />}
                        />

                        <Table.Column
                            dataIndex={["zoneManagement", "name"]}
                            title="Bölge Müdürlüğü"
                            ellipsis={{
                                tooltip: true
                            }}
                            width={120}
                            render={(zoneManagementName, row) => (
                                <>
                                    <Shortener
                                        text={zoneManagementName}
                                        length={12}
                                        render={text => <span>{text}</span>}
                                    />
                                </>
                            )}
                        />
                        <Table.Column
                            dataIndex="cityName"
                            title="Şehir / İlçe"
                            ellipsis={{
                                tooltip: true
                            }}
                            width={80}
                            render={(cityName, row) => (
                                <>
                                    <Shortener
                                        text={toTurkishProperCase(cityName) || "-"}
                                        length={32}
                                    />
                                    <br />
                                    <Shortener
                                        text={toTurkishProperCase(row?.districtName) || "-"}
                                        length={12}
                                    />
                                </>
                            )}
                        />

                        <Table.Column
                            dataIndex={["company", "companyName"]}
                            title="Firma / Alt Firma"
                            ellipsis={{
                                tooltip: true
                            }}
                            width={50}
                            render={(companyName, row) => (
                                <>
                                    <Shortener
                                        text={companyName || "-"}
                                        length={12}
                                        render={text => (
                                            <Link
                                                to={`/companies/${row?.company?._id}`}
                                                target="_blank"
                                            >
                                                {text}
                                            </Link>
                                        )}
                                    />
                                    <br />
                                    <Shortener
                                        text={row?.subCompany?.name || "-"}
                                        length={12}
                                        render={text => (
                                            <Link
                                                to={`/companies/sub-company/${row?.subCompany?._id}`}
                                                target="_blank"
                                            >
                                                {text}
                                            </Link>
                                        )}
                                    />
                                </>
                            )}
                        />
                        <Table.Column
                            dataIndex={["workingModel", "label", "tr"]}
                            title="Çalışma Modeli"
                            ellipsis={{
                                tooltip: true
                            }}
                            width={50}
                            render={workingModelName => (
                                <Shortener text={workingModelName || "-"} length={12} />
                            )}
                        />
                    </Table>
                </Row>
            </Modal>
            <MissingCourierFieldsModal
                courierId={courier?._id}
                companyName={
                    availableCourierRequests?.data?.find(
                        request => request?._id?.toString() === selectedCourierRequestId?.toString()
                    )?.company?.companyName || ""
                }
                mustBeFilledFields={missingFields || []}
                visibility={(missingFields || [])?.length > 0}
                onClose={() => setMissingFields([])}
                onFieldsFilled={() => {
                    setMissingFields([]);
                    callbackFn();
                }}
            />
        </>
    );
};
